import React, { useCallback, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Form from '@atlaskit/form';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { ExperienceTrackerContext, AI_AUDIO_BRIEFING } from '@confluence/experience-tracker';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import type { CustomLengthType, CustomToneType } from '../../../../types/customizationTypes';

import { ToneSelector } from './ToneSelector';
import { DurationSelector } from './DurationSelector';

const styles = cssMap({
	grow: {
		flex: 1,
	},
});

const i18n = defineMessages({
	applyButtonLabel: {
		id: 'audio.audio-briefing-popup.customize-tab.apply',
		defaultMessage: 'Apply customizations',
		description: 'Label for the button used to apply briefing customizations',
	},
});

interface CustomizationFormValues {
	length: CustomLengthType;
	tone: {
		value: CustomToneType;
	};
}

export const AudioBriefingPopupCustomizePanel = ({
	audioRef,
}: {
	audioRef: React.RefObject<HTMLAudioElement>;
}) => {
	const { formatMessage, locale } = useIntl();

	const [{ activeContent, streamEnded, hasFetched }, { fetchBriefing, setCustomization }] =
		useAudioBriefing();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { trackAIInteractionInit, trackAIInteractionDismiss } = useAIEventsInstrumentation();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendAnalyticsUIEvent = useCallback(
		(
			actionSubjectId: string,
			data: CustomizationFormValues,
			additionalAttributes?: Record<string, number | string>,
		) => {
			const attributes = {
				audioLocale: locale,
				length: data.length,
				tone: data.tone.value,
				hasFetchedBriefing: hasFetched ?? false,
				...additionalAttributes,
			};
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'customizePanel',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, locale, hasFetched],
	);

	const handleStartNewBriefing = (data: CustomizationFormValues) => {
		if (audioRef?.current) {
			audioRef.current.pause();
		}
		setCustomization({
			length: data.length,
			tone: data.tone.value,
		});

		if (!streamEnded) {
			experienceTracker.abort({
				name: AI_AUDIO_BRIEFING,
				reason: 'briefing customization was applied',
			});
			trackAIInteractionDismiss();
		}

		experienceTracker.start({
			name: AI_AUDIO_BRIEFING,
			attributes: {
				contentId: activeContent?.id,
			},
		});
		trackAIInteractionInit();
		sendAnalyticsUIEvent('audioCustomize', data);
		fetchBriefing();
	};

	return (
		<Box xcss={styles.grow}>
			<Form<CustomizationFormValues> onSubmit={handleStartNewBriefing}>
				{({ formProps }) => (
					<form {...formProps}>
						<Stack space="space.100">
							<ToneSelector />
							<DurationSelector />
							<Box paddingBlockStart="space.150">
								<Button type="submit" appearance="primary" shouldFitContainer>
									{formatMessage(i18n.applyButtonLabel)}
								</Button>
							</Box>
						</Stack>
					</form>
				)}
			</Form>
		</Box>
	);
};
